import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard, Package, Tags, Boxes, Handshake, Link2, Truck,
  MessageSquare, ShoppingCart, TrendingUp, Ban, PiggyBank, DollarSign,
  CreditCard, FileText, Building2, ClipboardList, Settings, Receipt,
  PanelLeftClose, PanelLeftOpen, LogOut, Menu, X,
} from 'lucide-react';

const BRANDING_KEY = 'ozon_branding';
const SIDEBAR_KEY  = 'ozon_sidebar_collapsed';
const BASE_URL = '/api';

function useBranding() {
  function readCache() {
    try { return JSON.parse(localStorage.getItem(BRANDING_KEY)) || {}; } catch { return {}; }
  }
  const [branding, setBranding] = useState(readCache);
  useEffect(() => {
    const token = localStorage.getItem('ozon_token');
    if (!token) return;
    fetch(`${BASE_URL}/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (!data) return; localStorage.setItem(BRANDING_KEY, JSON.stringify(data)); setBranding(data); })
      .catch(() => {});
  }, []);
  useEffect(() => {
    const handler = () => setBranding(readCache());
    window.addEventListener('branding-update', handler);
    return () => window.removeEventListener('branding-update', handler);
  }, []);
  useEffect(() => {
    const name = branding.appName || 'Ozon WMS';
    document.title = name;
    const favicon = document.getElementById('favicon');
    if (!favicon) return;
    if (branding.logoDataUrl) {
      favicon.href = branding.logoDataUrl;
    } else {
      const canvas = document.createElement('canvas');
      canvas.width = 32; canvas.height = 32;
      const ctx = canvas.getContext('2d');
      ctx.font = '24px serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('📦', 16, 16);
      favicon.href = canvas.toDataURL();
    }
  }, [branding]);
  return branding;
}

// 03.11.2026: emoji ikonlar lucide ikon setine geçti. Emoji her işletim sisteminde
// farklı çiziliyordu ve hizası satırdan satıra kayıyordu; vektör ikon tek boyda durur.
const navGroups = [
  {
    label: 'Ana menü',
    items: [{ to: '/', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    label: 'Operasyon',
    items: [
      { to: '/orders',            label: 'Siparişler',       icon: Package },
      { to: '/products',          label: 'Ürünler',          icon: Tags },
      { to: '/stock-hub',         label: 'Stok Yönetimi',    icon: Boxes },
      { to: '/scan-inbound',      label: 'Tedarik Takibi',   icon: Handshake },
      { to: '/supply-management', label: 'Tedarik Yönetimi', icon: Link2 },
      { to: '/musteri-proforma',  label: 'Müşteri Proformaları', icon: Receipt },
      { to: '/delivery-tracking', label: 'Teslimat Takibi',  icon: Truck },
      { to: '/messages',          label: 'Mesajlar',         icon: MessageSquare },
      { to: '/need-scan',         label: 'Alım Listesi',     icon: ShoppingCart },
    ],
  },
  {
    label: 'Finans & analiz',
    items: [
      { to: '/analytics',       label: 'Analiz',            icon: TrendingUp },
      { to: '/cancel-analysis', label: 'İptal Analizi',     icon: Ban },
      { to: '/profitability',   label: 'Karlılık',          icon: PiggyBank },
      { to: '/prices',          label: 'Fiyatlar',          icon: DollarSign },
      { to: '/ozon-finance',    label: 'Ozon Finans',       icon: CreditCard },
      { to: '/financials',      label: 'Finansal Detay',    icon: FileText },
      { to: '/company-finance', label: 'Şirket Finansları', icon: Building2 },
    ],
  },
  {
    label: 'Sistem',
    items: [
      { to: '/activity-log', label: 'Sipariş Aktiviteleri', icon: ClipboardList },
      { to: '/settings',     label: 'Ayarlar',              icon: Settings },
    ],
  },
];

function usePendingCount() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    function fetch_() {
      const token = localStorage.getItem('ozon_token');
      if (!token) return;
      fetch('/api/incoming-requests?status=pending', {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.ok ? r.json() : [])
        .then(d => setCount(Array.isArray(d) ? d.length : 0))
        .catch(() => {});
    }
    fetch_();
    const id = setInterval(fetch_, 60000); // her dk yenile
    return () => clearInterval(id);
  }, []);
  return count;
}

export default function Layout({ children }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const username  = localStorage.getItem('ozon_username') || 'admin';
  const branding  = useBranding();
  const pendingCount = usePendingCount();
  const appName   = branding.appName    || 'Ozon WMS';
  const appSub    = branding.appSubtitle || 'FBS Sipariş Yönetimi';
  const logoUrl   = branding.logoDataUrl || '';

  const [sidebarOpen, setSidebarOpen] = useState(false);          // mobil çekmece
  // Daraltılmış kenar çubuğu tercihi tarayıcıda kalır — her açılışta yeniden
  // daraltmak gerekmesin. Okunamazsa açık kabul edilir.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_KEY, collapsed ? '1' : '0'); } catch { /* özel pencere */ }
  }, [collapsed]);

  // Sayfa değişince çekmeceyi kapat (mobil)
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  function handleLogout() {
    localStorage.removeItem('ozon_token');
    localStorage.removeItem('ozon_username');
    navigate('/login');
  }

  const aktifSayfa = navGroups.flatMap((g) => g.items)
    .find((i) => (i.to === '/' ? location.pathname === '/' : location.pathname.startsWith(i.to)));

  const SidebarContent = ({ dar = false }) => (
    <>
      {/* Logo / başlık */}
      <div className={`flex-shrink-0 border-b border-white/5 ${dar ? 'px-3 py-4' : 'px-4 py-4'}`}>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-brand-600/15 ring-1 ring-brand-500/25 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {logoUrl
              ? <img src={logoUrl} alt="" className="w-full h-full object-contain" />
              : <Package size={18} className="text-brand-300" />}
          </div>
          {!dar && (
            <div className="min-w-0">
              <div className="text-sm font-semibold tracking-tight text-white truncate">{appName}</div>
              <div className="text-2xs text-slate-400 mt-0.5 truncate">{appSub}</div>
            </div>
          )}
        </div>
      </div>

      {/* Gezinme */}
      <nav className={`flex-1 overflow-y-auto py-4 ${dar ? 'px-2' : 'px-3'}`}>
        {navGroups.map((group) => (
          <div key={group.label} className="mb-5 last:mb-0">
            {!dar && (
              <div className="px-3 mb-2 text-2xs font-semibold tracking-wider text-slate-500 uppercase">
                {group.label}
              </div>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const rozet = item.to === '/supply-management' && pendingCount > 0;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    title={dar ? item.label : undefined}
                    className={({ isActive }) =>
                      `group relative flex items-center rounded-lg text-sm font-medium transition-all duration-150 ${
                        dar ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2'
                      } ${
                        isActive
                          ? 'bg-brand-600 text-white shadow-[0_1px_8px_rgb(37_99_235/0.45)]'
                          : 'text-slate-400 hover:bg-white/5 hover:text-white'
                      }`
                    }
                  >
                    <Icon size={17} strokeWidth={2} className="flex-shrink-0" />
                    {!dar && <span className="flex-1 truncate">{item.label}</span>}
                    {rozet && (
                      <span className={`bg-rose-500 text-white text-2xs font-bold leading-none rounded-full ${
                        dar ? 'absolute top-1.5 right-2 w-2 h-2 p-0' : 'px-1.5 py-0.5'
                      }`}>
                        {!dar && pendingCount}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Kullanıcı */}
      <div className={`flex-shrink-0 border-t border-white/5 py-3 ${dar ? 'px-2' : 'px-3'}`}>
        <div className={`flex items-center rounded-lg ${dar ? 'justify-center' : 'gap-2.5 px-2 py-1.5'}`}>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center text-xs font-bold text-white uppercase flex-shrink-0">
            {username[0]}
          </div>
          {!dar && (
            <>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-slate-200 truncate">{username}</div>
                <div className="text-2xs text-slate-500">Yönetici</div>
              </div>
              <button onClick={handleLogout} title="Çıkış yap"
                className="p-1.5 rounded-md text-slate-500 hover:text-rose-400 hover:bg-white/5 transition-colors">
                <LogOut size={15} />
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">

      {/* ── Masaüstü kenar çubuğu ─────────────────────────────────── */}
      <aside className={`hidden md:flex bg-slate-950 flex-col flex-shrink-0 h-screen sticky top-0
                         transition-[width] duration-200 ease-out ${collapsed ? 'w-[68px]' : 'w-60'}`}>
        <SidebarContent dar={collapsed} />
      </aside>

      {/* ── Mobil örtü + çekmece ──────────────────────────────────── */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setSidebarOpen(false)} />
      )}
      <aside className={`fixed top-0 left-0 h-full w-72 bg-slate-950 flex flex-col z-50
        transform transition-transform duration-300 ease-out md:hidden
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <button onClick={() => setSidebarOpen(false)}
          className="absolute top-4 right-3 p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/5">
          <X size={18} />
        </button>
        <SidebarContent />
      </aside>

      {/* ── Ana içerik ────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Üst şerit — yapışkan; masaüstünde daraltma düğmesi ve sayfa adı taşır */}
        <header className="sticky top-0 z-30 flex items-center gap-3 px-4 h-14 flex-shrink-0
                           bg-white/85 backdrop-blur border-b border-slate-200/80">
          <button onClick={() => setSidebarOpen(true)}
            className="md:hidden p-2 -ml-2 rounded-lg text-slate-600 hover:bg-slate-100" aria-label="Menü">
            <Menu size={20} />
          </button>
          <button onClick={() => setCollapsed((v) => !v)}
            className="hidden md:flex p-2 -ml-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            title={collapsed ? 'Menüyü genişlet' : 'Menüyü daralt'}>
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>

          <div className="flex items-center gap-2 min-w-0">
            {aktifSayfa && (
              <>
                <aktifSayfa.icon size={16} className="text-slate-400 flex-shrink-0" />
                <span className="text-sm font-medium text-slate-700 truncate">{aktifSayfa.label}</span>
              </>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2 md:hidden">
            {logoUrl && <img src={logoUrl} alt="" className="w-6 h-6 object-contain rounded" />}
            <span className="font-semibold text-sm text-slate-700 truncate">{appName}</span>
          </div>
        </header>

        <main className="flex-1 overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
}
